import * as Sentry from '@sentry/browser';
import { useFlags } from 'launchdarkly-react-client-sdk';
import get from 'lodash/get';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps, Redirect } from 'react-router-dom';

import { appClient } from 'src/config/apollo';
import { config } from 'src/config/config';
import { connectToAnExpertService, toastNotificationsService } from 'src/config/rootService';
import { Dispatch } from 'src/config/rootStore';
import {
    CareOption,
    CareRecommendationRecommendation,
    CareRecommendationState,
    FeelingsAboutTherapyEnum,
} from 'src/generated/webapp_gql';
import { useAcceptCareRecommendationMutation } from 'src/modules/onboarding/documents/acceptCareRecommendationMutation.generated';
import { WorkWithProviderIntro } from 'src/modules/providers/components/WorkWithProviderIntro';
import { useAddMemberCarePreferenceMutation } from 'src/modules/user/documents/addMemberCarePreferenceMutation.generated';
import { ProviderType } from 'src/modules/user/models/user';
import {
    getAdditionalInfoProvided,
    getCategorySelections,
    getMostRecentCareRecommendation,
    getUserInformation,
} from 'src/modules/user/selectors/userSelectors';
import { useDoesUserQualifyForTherapy } from 'src/modules/user/hooks/userHooks';
import { ScheduleSessionCalendarPageLocationState } from 'src/modules/videoSessions/pages/ScheduleSessionCalendarPage';
import { getSessionUtilizationInfo } from 'src/modules/videoSessions/selectors/videoSessionsSelectors';
import { DisclaimerModal } from 'src/shared/components/DisclaimerModal';

import { AdditionalInfo } from '../components/AdditionalInfo';
import { RequestProvider } from '../components/RequestProvider';
import { LocalTherapyRequest } from '../components/LocalTherapyRequest';
import { NoTherapySupport } from '../components/NoTherapySupport';
import { ProviderSelection } from '../components/ProviderSelection';
import { TherapyMotivation } from '../components/TherapyMotivation';
import { TherapySupportWith } from '../components/TherapySupportWith';
import { WorkWithTherapistRequestSent } from '../components/WorkWithTherapistRequestSent';
import { useProvider } from 'src/modules/providers/hooks/useProvider';

enum Step {
    INTRO = 'intro',
    SUPPORT_WITH = 'supportWith',
    ADDITIONAL_INFO = 'additionalInfo',
    MOTIVATION = 'motivation',

    // in-app-therapy
    SELECT_THERAPIST = 'selectTherapist',
    LOCAL_THERAPY_REQUEST = 'localTherapyRequest',

    // no in-app-therapy
    REQUEST_PROVIDER = 'requestProvider',
    REQUEST_SENT = 'requestSent',
}

enum Country {
    CHINA = 'China',
}

type Props = RouteComponentProps<
    {},
    {},
    {
        therapistToRemoveUuid?: string;
        onboarding?: boolean;
        disableCheck?: boolean; // used by the for you action to disable qualifiesForTherapy check since this already being verified on the BE and we want to avoid conflicts between FE and BE checks
    }
>;

// [Important] TODO: Add component tests -- https://joinmodernhealth.atlassian.net/browse/FOUNDATION-197
const WorkWithTherapistPage: React.FC<Props> = ({ history, location }) => {
    const { t } = useTranslation();

    const therapistToRemoveUuid = location?.state?.therapistToRemoveUuid;

    const {
        fetchProviders,
        removeProvider,
        addProvider,
        matchedPreferenceProviders,
        notMatchedPreferenceProviders,
        isMatchedProvidersLoading,
        allowsFiltering,
        matchedAttempt,
        myTherapists,
    } = useProvider({ providerType: CareOption.Therapy });
    const [step, setStep] = useState(therapistToRemoveUuid ? Step.SELECT_THERAPIST : Step.INTRO);
    const [acceptCareRecommendation] = useAcceptCareRecommendationMutation({
        client: appClient,
    });
    const { careRecommendations, country } = useSelector(getUserInformation);

    const flags = useFlags();

    const shouldShowDisclaimer = flags?.showChineseDisclaimer && Country.CHINA === country;

    const [savingCarePreference, setSavingCarePreference] = useState(false);
    const [supportWithSpecialties, setSupportWithSpecialties] = useState<string[]>([]);
    const [supportWithOtherSpecialties, setSupportWithOtherSpecialties] = useState('');
    const [feelingsAboutTherapy, setFeelingsAboutTherapy] = useState('');
    const [requestProvider, setRequestProvider] = useState('');
    const [selectedTherapistUuid, setSelectedTherapistUuid] = useState('');
    const [showDisclaimer, setShowDisclaimer] = useState(shouldShowDisclaimer);
    const dispatch = useDispatch<Dispatch>();
    const user = useSelector(getUserInformation);
    const requiredInfoProvided = useSelector(getAdditionalInfoProvided);
    const qualifiesForTherapySelectorResult = useDoesUserQualifyForTherapy();
    const careRecommendation = useSelector(getMostRecentCareRecommendation);
    const phoneNumber = user?.phoneNumber;
    const categories = useSelector(getCategorySelections);
    const { therapyVisitsRemaining, coveredTherapyVisits } = useSelector(getSessionUtilizationInfo);
    const isMemberEligibleForHpi = user.isCompanyHpiEnabled && user.hpiEligibilityStatus;
    const showNoTherapySupport = therapyVisitsRemaining < 1 && !isMemberEligibleForHpi;

    const disableCheck = useMemo(() => {
        const searchParams = new URLSearchParams(location.search);
        const queryKey = 'disableCheck';
        if (searchParams.has(queryKey)) {
            return searchParams.get(queryKey) === 'true';
        }

        return Boolean(location.state?.disableCheck);
    }, [location.search, location.state]);
    const qualifiesForTherapy = qualifiesForTherapySelectorResult || disableCheck;

    const [addMemberCarePreferenceMutation] = useAddMemberCarePreferenceMutation({
        client: appClient,
        variables: {
            feelingsAboutTherapy: feelingsAboutTherapy as FeelingsAboutTherapyEnum,
            specialtiesSelected: supportWithSpecialties,
            otherSpecialtiesFreeform: supportWithOtherSpecialties,
        },
    });

    useEffect(() => {
        if (myTherapists?.length >= 1 && step === Step.INTRO && !therapistToRemoveUuid) {
            toastNotificationsService.renderErrorToast(
                t('providers:provider_selection.notification.max_therapists'),
            );
            history.replace('/');
        }
    }, [myTherapists, step, therapistToRemoveUuid, history, t]);

    // do not show intro steps again when user decided
    // to go back to therapist selection from session scheduling
    useEffect(() => {
        if (step === Step.INTRO && history.action === 'POP' && qualifiesForTherapy) {
            setStep(Step.SELECT_THERAPIST);
        }
    }, [step, history, qualifiesForTherapy]);

    useEffect(() => {
        if (
            !qualifiesForTherapy &&
            step !== Step.SELECT_THERAPIST &&
            careRecommendation?.recommendation !== CareRecommendationRecommendation.Therapy
        ) {
            history.replace('/');
        }
    }, [qualifiesForTherapy, step, careRecommendation, history]);

    const handleSupportWithTilePress = (text: string) => {
        setSupportWithSpecialties(specialties => {
            const newSpecialties = specialties.includes(text)
                ? specialties.filter(value => value !== text)
                : [...specialties, text];

            return newSpecialties;
        });
    };

    const handleCarePreferenceSubmit = async () => {
        setSavingCarePreference(true);

        try {
            await addMemberCarePreferenceMutation();
            if (flags?.showDemographicsTherapyWeb) {
                setStep(Step.SELECT_THERAPIST);
            } else {
                if (careRecommendation?.uuid) {
                    const res = await fetchProviders(careRecommendation?.uuid, []);
                    setSavingCarePreference(false);

                    const nextStep = (res?.matchedProviders?.providersMatchedPreferences ?? [])
                        .length
                        ? Step.SELECT_THERAPIST
                        : Step.REQUEST_PROVIDER;

                    setStep(nextStep);
                } else {
                    setStep(Step.REQUEST_PROVIDER);
                }
            }
        } catch {
            toastNotificationsService.renderErrorToast(config.messages.genericError);
        }
    };

    const handleProviderRequestStepSubmit = async () => {
        await connectToAnExpertService.submitForm({
            userInfo: user,
            therapistRequested: true,
            anythingElse: requestProvider,
            careRecUuid: careRecommendation?.uuid,
            phoneNumber,
            categories,
            tags: ['Therapy_flow_additional_information'],
        });

        setStep(Step.REQUEST_SENT);
    };

    const handleLocalTherapyRequestSubmit = async (additionalInfo: string) => {
        await connectToAnExpertService.submitForm({
            userInfo: user,
            therapistRequested: true,
            anythingElse: additionalInfo,
            careRecUuid: careRecommendation?.uuid,
            phoneNumber,
            categories,
            tags: ['Therapy_flow_local_therapy_request'],
        });

        setStep(Step.REQUEST_SENT);
    };

    const handleAddTherapist = async (therapistUuid: string) => {
        if (!therapistUuid) {
            return;
        }

        if (therapistToRemoveUuid) {
            await removeProvider(therapistToRemoveUuid);
        }

        const success = await addProvider(therapistUuid, matchedAttempt);

        if (!success) {
            if (careRecommendation?.uuid) {
                await fetchProviders(careRecommendation?.uuid, []);
            }

            setSelectedTherapistUuid('');
        }

        await dispatch.providers.fetchMyProviders();
        history.push('/schedule-session/calendar', {
            coachUuid: therapistUuid,
            isFirstSession: coveredTherapyVisits === therapyVisitsRemaining,
            removeCoachAssignmentOnBackPress: therapistToRemoveUuid ? false : true,
            disableBack: therapistToRemoveUuid ? true : false,
            onboarding: location.state?.onboarding || false,
        } as ScheduleSessionCalendarPageLocationState);
    };

    useEffect(() => {
        if (careRecommendation?.uuid && (step === Step.SELECT_THERAPIST || therapistToRemoveUuid)) {
            fetchProviders(careRecommendation.uuid, []);
        }
    }, [step]);

    const handleTherapistPress = async (therapistUuid: string) => {
        if (selectedTherapistUuid) {
            return;
        }

        setSelectedTherapistUuid(therapistUuid);
        handleAddTherapist(therapistUuid);
    };

    const handleContinue = async () => {
        try {
            // for users that have not gone through the new onboarding so dont have any care recs
            if (!careRecommendations[0]?.uuid) {
                setStep(Step.SUPPORT_WITH);
                return;
            }

            const res = await acceptCareRecommendation({
                variables: {
                    uuid: careRecommendations[0].uuid,
                    outcome: CareOption.Therapy,
                },
            });
            const careUuid = get(res, 'data.acceptCareRecommendation.careRecommendation.uuid');
            if (!careUuid) {
                throw new Error('Failed to accept care recommendation');
            }

            setStep(Step.SUPPORT_WITH);
        } catch (error) {
            setStep(Step.SUPPORT_WITH);
            toastNotificationsService.renderErrorToast(config.messages.genericError);

            Sentry.withScope(scope => {
                scope.setExtra('error_info', 'Error: failed to accept a care recommendation');
                Sentry.captureException(error);
            });
        }
    };

    const changeStep = (nextStep: Step) => () => setStep(nextStep);

    // onboarding shortcut experiment -- user has to finish onboarding first
    if (careRecommendation?.state === CareRecommendationState.BypassedAssessments) {
        return (
            <Redirect
                to={{
                    pathname: '/a-few-questions',
                    state: { matchAttempt: true },
                }}
            />
        );
    }

    if (showNoTherapySupport) {
        return <NoTherapySupport />;
    }

    const showPreferenceFilters = !!(flags?.showDemographicsTherapyWeb && allowsFiltering);

    const renderStep = () => {
        switch (step) {
            case Step.INTRO:
                return (
                    <WorkWithProviderIntro
                        providerType={ProviderType.THERAPIST}
                        onChooseProviderClick={handleContinue}
                        onNotNowClick={() => {
                            history.push('/dashboard');
                        }}
                        description={t('providers:therapist.work_with.description')}
                    />
                );
            case Step.SUPPORT_WITH:
                return (
                    <TherapySupportWith
                        supportWithSpecialties={supportWithSpecialties}
                        supportWithOtherSpecialties={supportWithOtherSpecialties}
                        onTilePress={handleSupportWithTilePress}
                        onTextAreaChange={setSupportWithOtherSpecialties}
                        onPreviousPress={changeStep(Step.INTRO)}
                        onContinuePress={changeStep(
                            requiredInfoProvided ? Step.MOTIVATION : Step.ADDITIONAL_INFO,
                        )}
                    />
                );

            case Step.ADDITIONAL_INFO:
                return (
                    <AdditionalInfo
                        onSubmit={changeStep(Step.MOTIVATION)}
                        onPreviousPress={changeStep(Step.SUPPORT_WITH)}
                    />
                );

            case Step.MOTIVATION:
                return (
                    <TherapyMotivation
                        onContinuePress={handleCarePreferenceSubmit}
                        onPreviousPress={changeStep(Step.ADDITIONAL_INFO)}
                        feelingsAboutTherapy={feelingsAboutTherapy}
                        onCardPress={setFeelingsAboutTherapy}
                        loading={savingCarePreference}
                    />
                );

            case Step.SELECT_THERAPIST:
                const teletherapyMode =
                    matchedPreferenceProviders.length > 0 &&
                    matchedPreferenceProviders.every(
                        provider =>
                            provider.matchAlgorithmVersion &&
                            provider.matchAlgorithmVersion.includes('teletherapist-only-algorithm'),
                    );
                let subtitle = 'providers:provider_selection.description.covid';

                if (teletherapyMode) {
                    subtitle = 'providers:provider_selection.description.teletherapy';
                }

                return (
                    <ProviderSelection
                        visible={step === Step.SELECT_THERAPIST}
                        onProviderPress={handleTherapistPress}
                        providers={[]}
                        selectedProvider={selectedTherapistUuid}
                        loadingMatches={isMatchedProvidersLoading}
                        showMatchedProviders
                        showFilters={showPreferenceFilters}
                        matchedProviders={matchedPreferenceProviders}
                        notMatchedProviders={notMatchedPreferenceProviders}
                        refetchMatchedProviders={races =>
                            careRecommendation?.uuid &&
                            fetchProviders(careRecommendation.uuid, races)
                        }
                        onBackPress={() => {
                            setStep(Step.MOTIVATION);
                        }}
                        showBackButton={location?.state?.onboarding}
                        headerText={t('providers:provider_selection.title.not_matched.therapist')}
                        subTitleText={t(subtitle)}
                        teletherapyMode={teletherapyMode}
                        onNoProviders={() => {
                            setStep(Step.REQUEST_PROVIDER);
                        }}
                    />
                );

            case Step.LOCAL_THERAPY_REQUEST:
                return (
                    <LocalTherapyRequest
                        onBackClick={changeStep(Step.SELECT_THERAPIST)}
                        onSubmit={handleLocalTherapyRequestSubmit}
                    />
                );

            case Step.REQUEST_PROVIDER:
                return (
                    <RequestProvider
                        anythingElse={requestProvider}
                        onTextAreaChange={setRequestProvider}
                        onSubmit={handleProviderRequestStepSubmit}
                        providerType={ProviderType.THERAPIST}
                    />
                );

            case Step.REQUEST_SENT:
                return <WorkWithTherapistRequestSent />;

            default:
                return null;
        }
    };

    return (
        <>
            {renderStep()}
            <DisclaimerModal
                visible={showDisclaimer}
                onClick={() => setShowDisclaimer(false)}
                description={t('shared:disclaimer_modal.description', { returnObjects: true })}
                buttonText={t('shared:disclaimer_modal.buttonText')}
            />
        </>
    );
};

export default WorkWithTherapistPage;
